import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

export const JoinTheWaitlist = () => {
  const { scrollY } = useScroll();
  // Subtle parallax: make the whole panel float slightly as you scroll
  const y = useTransform(scrollY, [0, 500], [0, 50]);

  // Generate some floating particles (behind panel)
  const particles = Array.from({ length: 10 }).map((_, i) => ({
    id: i,
    size: Math.random() * 40 + 20,
    top: `${Math.random() * 100}%`,
    left: `${Math.random() * 100}%`,
    delay: Math.random() * 5,
    duration: 20 + Math.random() * 10,
  }));

  return (
    <motion.div
      style={{ y }}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 1 }}
      className="relative mx-4 md:mx-24 z-20"
    >
      {/* Background aura & particles */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        {/* Soft glowing gradient aura */}
        <div className="absolute w-full h-full rounded-xl bg-gradient-to-r from-[#F7931E]/15 to-transparent blur-3xl animate-pulse"></div>
        {/* Floating glowing particles */}
        {particles.map((p) => (
          <motion.div
            key={p.id}
            initial={{ y: 0, opacity: 0.4 }}
            animate={{ y: ['-5%', '5%', '-5%'], opacity: [0.3, 0.6, 0.3] }}
            transition={{
              repeat: Infinity,
              duration: p.duration,
              delay: p.delay,
              ease: 'easeInOut',
            }}
            style={{
              position: 'absolute',
              top: p.top,
              left: p.left,
              width: p.size,
              height: p.size,
              background: 'radial-gradient(circle, rgba(247,147,30,0.5), transparent 70%)',
              borderRadius: '50%',
              filter: 'blur(20px)',
            }}
          />
        ))}
      </div>

      {/* Waitlist Panel */}
      <div className="bg-[#F7931E] rounded-xl shadow-2xl p-8 md:p-12 text-black relative overflow-hidden border border-[#FFAF52]/30">
        {/* Decorative top pulse line */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#FFDA9E] via-[#F7931E] to-transparent animate-pulse"></div>

        {/* Headline */}
        <div className="mb-6 text-center">
          <motion.h2
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="text-3xl sm:text-5xl font-bold leading-snug drop-shadow-md"
          >
            Fuel, finance, and trade smarter.
          </motion.h2>
          <p className="mt-4 max-w-2xl mx-auto text-lg">
            Join a growing network of energy providers, contractors, and operators shaping the
            future of payments.
          </p>
        </div>

        {/* Form */}
        <form className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {['First Name', 'Last Name', 'Email Address', 'Select Category'].map(
              (placeholder, idx) => (
                <motion.input
                  key={idx}
                  whileFocus={{ scale: 1.03, borderColor: '#2D2D2D' }}
                  transition={{ duration: 0.2 }}
                  type={placeholder.includes('Email') ? 'email' : 'text'}
                  placeholder={placeholder}
                  className="bg-[#FFAF52] rounded-lg p-4 w-full placeholder:text-black placeholder:font-bold focus:outline-none border-2 border-transparent focus:border-[#2D2D2D] transition"
                />
              )
            )}
          </div>

          {/* Checkboxes */}
          <div className="space-y-2 text-sm">
            <label className="flex items-start space-x-2 cursor-pointer">
              <input type="checkbox" className="mt-1 w-4 h-4 accent-[#2D2D2D]" />
              <span>
                Send me updates about releases and promotions for LonaPay products and services.
              </span>
            </label>
            <label className="flex items-start space-x-2 cursor-pointer">
              <input type="checkbox" className="mt-1 w-4 h-4 accent-[#2D2D2D]" />
              <span>Add me to mobile app Beta testing.</span>
            </label>
          </div>

          {/* Privacy Note */}
          <p className="text-xs text-black/80">
            We respect your privacy. Your information will never be sold or misused.
          </p>

          {/* Call-to-Action Button with glowing pulse */}
          <motion.button
            animate={{
              boxShadow: [
                '0 0 0px rgba(247,147,30,0.0)',
                '0 0 20px rgba(247,147,30,0.5)',
                '0 0 0px rgba(247,147,30,0.0)',
              ],
            }}
            transition={{ duration: 2, repeat: Infinity }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="w-fit px-12 py-4 bg-[#2D2D2D] text-white rounded-lg text-lg font-bold tracking-wide mt-4 hover:bg-black transition"
          >
            Join the Waitlist
          </motion.button>
        </form>
      </div>
    </motion.div>
  );
};
